import { Link, useCanGoBack, useRouter } from '@tanstack/react-router'

import { ArrowLeft, Home } from 'lucide-react'

import { Button } from '../ui/button'

type BackOrHomeButtonProps = {
	/**
	 * Error surfaces pair every action with an icon; the root error screen
	 * renders its buttons bare, so the icon is opt-in rather than assumed.
	 */
	withIcon?: boolean
}

/**
 * The "Go Back" action for the 403 / 404 error surfaces.
 *
 * A cold load — deep link, hard refresh, new tab — has no in-app entry to step
 * back to, so an unconditional `history.back()` walks the user out of the app.
 * When there is nothing to step back to, this sends them to `/home` instead and
 * relabels itself, so the button never promises a destination it cannot reach.
 *
 * `BackLink` solves the same problem for in-page navigation, but its button is
 * a fixed icon square with its own responsive label — the shapes do not merge.
 */
export const BackOrHomeButton = ({ withIcon }: BackOrHomeButtonProps) => {
	const router = useRouter()
	const canGoBack = useCanGoBack()

	if (!canGoBack) {
		return (
			<Button
				variant="outline"
				nativeButton={false}
				render={<Link to="/home" />}
			>
				{withIcon && <Home className="mr-1.5 h-4 w-4" />}
				Go to Homepage
			</Button>
		)
	}

	return (
		<Button variant="outline" onClick={() => router.history.back()}>
			{withIcon && <ArrowLeft className="mr-1.5 h-4 w-4" />}
			Go Back
		</Button>
	)
}
